import { useState } from "react";
import { Sprout, Loader2, ArrowRight, RefreshCw } from "lucide-react";
import { pickPlants } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { MD } from "@/components/MD";

const STEPS = {
    en: [
        {
            key: "space",
            q: "Where are you growing?",
            opts: [
                { v: "windowsill", l: "Windowsill" },
                { v: "closet", l: "Closet w/ light" },
                { v: "classroom", l: "Classroom corner" },
                { v: "rooftop", l: "Rooftop / patio" },
                { v: "lot", l: "Outdoor lot" },
            ],
        },
        {
            key: "light",
            q: "Light source?",
            opts: [
                { v: "window", l: "Window only" },
                { v: "grow_light", l: "Grow light" },
                { v: "full_sun", l: "Full sun outside" },
            ],
        },
        {
            key: "ph_range",
            q: "Water pH range?",
            opts: [
                { v: "acidic", l: "Acidic (5.5–6.5)" },
                { v: "neutral", l: "Neutral (6.5–7.5)" },
                { v: "alkaline", l: "Alkaline (7.5+)" },
            ],
        },
        {
            key: "time_to_harvest",
            q: "How fast do you want results?",
            opts: [
                { v: "fast30", l: "30 days" },
                { v: "mid60", l: "60 days" },
                { v: "long90", l: "90+ days" },
            ],
        },
        {
            key: "goal",
            q: "What do you want?",
            opts: [
                { v: "leafy", l: "Leafy greens" },
                { v: "herbs", l: "Herbs" },
                { v: "fruit", l: "Fruit / veg" },
                { v: "flowers", l: "Flowers" },
            ],
        },
    ],
    es: [
        {
            key: "space",
            q: "¿Dónde vas a cultivar?",
            opts: [
                { v: "windowsill", l: "Repisa de ventana" },
                { v: "closet", l: "Armario con luz" },
                { v: "classroom", l: "Esquina del salón" },
                { v: "rooftop", l: "Azotea / patio" },
                { v: "lot", l: "Lote al aire libre" },
            ],
        },
        {
            key: "light",
            q: "¿Fuente de luz?",
            opts: [
                { v: "window", l: "Solo ventana" },
                { v: "grow_light", l: "Luz de cultivo" },
                { v: "full_sun", l: "Sol pleno" },
            ],
        },
        {
            key: "ph_range",
            q: "¿Rango de pH del agua?",
            opts: [
                { v: "acidic", l: "Ácido (5.5–6.5)" },
                { v: "neutral", l: "Neutro (6.5–7.5)" },
                { v: "alkaline", l: "Alcalino (7.5+)" },
            ],
        },
        {
            key: "time_to_harvest",
            q: "¿Qué tan rápido?",
            opts: [
                { v: "fast30", l: "30 días" },
                { v: "mid60", l: "60 días" },
                { v: "long90", l: "90+ días" },
            ],
        },
        {
            key: "goal",
            q: "¿Qué quieres cultivar?",
            opts: [
                { v: "leafy", l: "Hojas verdes" },
                { v: "herbs", l: "Hierbas" },
                { v: "fruit", l: "Fruta / vegetal" },
                { v: "flowers", l: "Flores" },
            ],
        },
    ],
};

const COPY = {
    en: {
        tag: "// plant picker · AI matched",
        h1a: "Tell us about your spot.",
        h1b: "We pick the perfect 3 plants.",
        sub: "5 quick questions. AI matches your space, light, water, and timeline to plants that will actually thrive — referencing the GROW LAB knowledge.",
        next: "Next",
        gen: "Generate Picks",
        generating: "Picking your plants…",
        again: "Start over",
        step: "Step",
        of: "of",
    },
    es: {
        tag: "// elector · IA emparejado",
        h1a: "Cuéntanos de tu lugar.",
        h1b: "Elegimos las 3 plantas ideales.",
        sub: "5 preguntas rápidas. La IA empareja espacio, luz, agua y tiempo con plantas que realmente prosperarán.",
        next: "Siguiente",
        gen: "Generar",
        generating: "Eligiendo tus plantas…",
        again: "Reiniciar",
        step: "Paso",
        of: "de",
    },
};

export default function PlantPicker() {
    const { lang } = useLang();
    const steps = STEPS[lang] || STEPS.en;
    const t = COPY[lang] || COPY.en;
    const [stepIdx, setStepIdx] = useState(0);
    const [answers, setAnswers] = useState({});
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);

    const cur = steps[stepIdx];

    const setAnswer = (v) => setAnswers((a) => ({ ...a, [cur.key]: v }));

    const next = async () => {
        if (stepIdx < steps.length - 1) {
            setStepIdx((s) => s + 1);
            return;
        }
        // Submit
        setLoading(true);
        try {
            const r = await pickPlants({ ...answers, lang });
            setResult(r);
        } catch {
            alert("Try again.");
        } finally {
            setLoading(false);
        }
    };

    const reset = () => {
        setStepIdx(0);
        setAnswers({});
        setResult(null);
    };

    if (result) {
        return (
            <main
                className="max-w-4xl mx-auto px-5 md:px-8 pt-10 md:pt-16 pb-24 relative"
                data-testid="plant-picker-result"
            >
                <div className="glow glow-lime -top-20 -left-20" />
                <div className="relative z-10">
                    <span className="tag">{t.tag}</span>
                    <h1 className="font-display font-black text-white text-4xl md:text-6xl mt-3 uppercase leading-[0.95]">
                        Your picks
                    </h1>
                    <div className="card p-6 md:p-8 mt-8">
                        <MD text={result.result_markdown} />
                    </div>
                    <button
                        onClick={reset}
                        className="btn-ghost mt-8"
                        data-testid="picker-reset"
                    >
                        <RefreshCw size={14} /> {t.again}
                    </button>
                </div>
            </main>
        );
    }

    return (
        <main
            className="max-w-3xl mx-auto px-5 md:px-8 pt-10 md:pt-16 pb-24 relative"
            data-testid="plant-picker-page"
        >
            <div className="glow glow-lime -top-20 -left-20" />
            <div className="relative z-10">
                <span className="tag">{t.tag}</span>
                <h1 className="font-display font-black text-white text-4xl md:text-6xl mt-3 uppercase leading-[0.95]">
                    {t.h1a}
                    <br />
                    <span className="font-brush text-[var(--lime)] inline-block -rotate-2">
                        {t.h1b}
                    </span>
                </h1>
                <p className="font-mono text-[var(--muted)] mt-4 max-w-2xl">{t.sub}</p>

                {/* Progress dots */}
                <div className="flex items-center gap-2 mt-8" data-testid="picker-progress">
                    {steps.map((_, i) => (
                        <span
                            key={i}
                            className={
                                "h-1 flex-1 " +
                                (i <= stepIdx
                                    ? "bg-[var(--lime)]"
                                    : "bg-[var(--border)]")
                            }
                        />
                    ))}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--muted)] mt-2">
                    {t.step} {stepIdx + 1} {t.of} {steps.length}
                </div>

                <div className="card p-6 md:p-8 mt-6" data-testid={`picker-step-${cur.key}`}>
                    <h2 className="font-display font-bold text-white text-2xl uppercase mb-5">
                        {cur.q}
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {cur.opts.map((o) => {
                            const active = answers[cur.key] === o.v;
                            return (
                                <button
                                    key={o.v}
                                    onClick={() => setAnswer(o.v)}
                                    data-testid={`picker-opt-${cur.key}-${o.v}`}
                                    className={
                                        "border-2 p-4 text-left font-mono transition-all " +
                                        (active
                                            ? "border-[var(--lime)] bg-[var(--lime)]/5 text-white"
                                            : "border-[var(--border)] hover:border-[var(--cyan)] text-[var(--text)]")
                                    }
                                >
                                    {o.l}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="flex justify-between items-center mt-6">
                    <button
                        onClick={() => setStepIdx((s) => Math.max(0, s - 1))}
                        disabled={stepIdx === 0}
                        className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--muted)] hover:text-white disabled:opacity-30"
                        data-testid="picker-back"
                    >
                        ← back
                    </button>
                    <button
                        onClick={next}
                        disabled={!answers[cur.key] || loading}
                        className="btn-prime"
                        data-testid="picker-next"
                    >
                        {loading ? (
                            <>
                                <Loader2 size={14} className="animate-spin" />
                                {t.generating}
                            </>
                        ) : stepIdx === steps.length - 1 ? (
                            <>
                                <Sprout size={14} /> {t.gen}
                            </>
                        ) : (
                            <>
                                {t.next} <ArrowRight size={14} />
                            </>
                        )}
                    </button>
                </div>
            </div>
        </main>
    );
}
